/**
 * What a vault's provisioning row actually means on screen.
 *
 * Creating a vault writes a request row and returns. The build itself is Terraform running in
 * the deployment account, reached by an EventBridge event that the egress monitoring Lambda
 * emits once a minute, with the outcome coming back as another event. So between "created" and
 * "Active" there are several states that all look identical if you only read OperationalStatus,
 * and one of them is a fault:
 *
 *   queued     a request row exists but nothing has emitted it yet (RequestedAt is NULL).
 *              A few seconds of this is normal. Minutes of it means the emitter is not running,
 *              and that is the failure this whole distinction exists to make visible — it is
 *              exactly what the estate looked like before, when the poller had no trigger at
 *              all and a created vault sat at "Provisioning" indefinitely.
 *   waiting    asked for, but sequenced behind its region, which is not operational yet.
 *   building   Terraform is running; stage n of m if the reply events say so.
 *   failed     something reported ProvisioningFailed. Without this the row would sit at
 *              "Provisioning" forever and the screen would show a vault being built that
 *              never will be.
 */

export interface VaultProvisioningFields {
    /** When the request row was written. A queued row is measured against this. */
    created?: string | Date | null
    operationalStatus?: string | null
    /** ProvisioningRequests.Status — the authoritative phase. */
    requestStatus?: string | null
    /** ProvisioningRequests.Stage — where the deployment account has got to. */
    buildStage?: string | null
    buildRequested?: string | Date | null
    buildStageNumber?: number | null
    buildStageCount?: number | null
    buildError?: string | null
}

export type ProvisioningPhase = 'none' | 'queued' | 'waiting' | 'building' | 'failed'

export interface ProvisioningState {
    phase: ProvisioningPhase
    /** Stage caption, e.g. "Creating account". Null when there is nothing specific to say. */
    stage: string | null
    /** 1-based stage position, when the deployment account reported one. */
    stageNumber: number | null
    stageCount: number | null
    error: string | null
    /** True once the wait is long enough to be worth questioning rather than watching. */
    stalled: boolean
}

/** Stage values the deployment account reports, mapped to something a person reads. */
const STAGE_LABELS: Record<string, string> = {
    Requested: 'Requested',
    WaitingForRegion: 'Waiting for its region',
    CreatingAccount: 'Creating account',
    MovingOU: 'Moving to organisational unit',
    OUMoved: 'Organisational unit set',
    PlanningTerraform: 'Planning',
    ApplyingTerraform: 'Applying',
    CreatingBuckets: 'Creating buckets',
    CreatingQueues: 'Creating queues',
    Complete: 'Complete',
    Failed: 'Failed',
}

export function stageLabel(stage?: string | null): string | null {
    const s = String(stage || '').trim()
    if (!s) return null
    return STAGE_LABELS[s] || s
}

/**
 * `now` is injected so the stalled threshold is testable rather than clock-dependent.
 * Ten minutes: a Terraform apply legitimately takes several, but nothing should sit UNASKED
 * for that long when the emitter runs every minute.
 */
export const STALLED_AFTER_MS = 10 * 60 * 1000

export function provisioningState(v: VaultProvisioningFields, now: number = Date.now()): ProvisioningState {
    const status = String(v?.operationalStatus || '')
    const requestStatus = String(v?.requestStatus || '')
    const error = v?.buildError ? String(v.buildError) : null

    // A failure is worth showing whatever the operational status says, because the two are
    // written by different events and can disagree if one is lost.
    if (error || requestStatus === 'Failed') {
        return { phase: 'failed', stage: stageLabel(v?.buildStage), stageNumber: null, stageCount: null, error, stalled: false }
    }

    // A completed request means the vault is built, whatever the vault row still says.
    if (requestStatus === 'Complete' || status !== 'Provisioning') {
        return { phase: 'none', stage: null, stageNumber: null, stageCount: null, error: null, stalled: false }
    }

    const requestedAt = v?.buildRequested ? new Date(v.buildRequested).getTime() : null
    const requested = requestedAt != null && !Number.isNaN(requestedAt)

    if (!requested) {
        // Nothing has emitted a request for this row yet. THIS is the state that exposes a
        // dead emitter, so it has to be able to go stalled — measured from when the row was
        // written, since there is no request timestamp to measure from.
        const createdAt = v?.created ? new Date(v.created).getTime() : null
        const age = createdAt != null && !Number.isNaN(createdAt) ? now - createdAt : 0
        return { phase: 'queued', stage: null, stageNumber: null, stageCount: null, error: null, stalled: age > STALLED_AFTER_MS }
    }

    const waiting = requestStatus === 'WaitingForRegion'
    const n = v?.buildStageNumber ?? null
    const m = v?.buildStageCount ?? null

    return {
        phase: waiting ? 'waiting' : 'building',
        stage: stageLabel(v?.buildStage || v?.requestStatus),
        stageNumber: n,
        stageCount: m,
        error: null,
        stalled: now - requestedAt > STALLED_AFTER_MS,
    }
}
